// Responsable Service - Gestión de usuarios asignables como responsables
// Endpoints: /admin/responsables/*

import { api } from './api';
import type {
  Responsable,
  ResponsablesConfigResponse,
  UpdateResponsablesRequest,
  ToggleResponsableRequest,
  EnabledResponsablesResponse,
} from '@/types/responsable';

export const responsableService = {
  /**
   * Obtener todos los usuarios elegibles como responsables con su estado actual
   * Solo admin puede acceder
   * GET /admin/responsables/config
   */
  async getResponsablesConfig(params?: {
    search?: string;
    role?: string;
  }): Promise<ResponsablesConfigResponse> {
    try {
      const { data } = await api.get<ResponsablesConfigResponse>('/admin/responsables/config', {
        params,
      });
      return data;
    } catch (error: any) {
      console.error('Error getting responsables config:', error);
      // Fallback: obtener usuarios del endpoint existente y mapear
      if (error.response?.status === 404) {
        return await this.fallbackGetResponsablesConfig(params);
      }
      throw error;
    }
  },

  /**
   * Fallback: si el endpoint de backend no existe aún,
   * usa el endpoint de usuarios y simula la configuración de responsables
   * guardando la config en localStorage hasta que el backend esté listo
   */
  async fallbackGetResponsablesConfig(params?: {
    search?: string;
    role?: string;
  }): Promise<ResponsablesConfigResponse> {
    try {
      // Obtener usuarios del endpoint existente (solo roles elegibles)
      const eligibleRoles = ['admin', 'lawyer', 'agent', 'superuser'];
      const roleFilter = params?.role && eligibleRoles.includes(params.role) ? params.role : undefined;
      
      const { data } = await api.get('/users/', {
        params: {
          limit: 200,
          is_active: true,
          role: roleFilter,
          search: params?.search,
        },
      });

      const users = data.items || (Array.isArray(data) ? data : []);
      
      // Obtener configuración guardada localmente
      const localConfig = this.getLocalConfig();

      // Filtrar solo roles elegibles
      const eligibleUsers = users.filter((u: any) => eligibleRoles.includes(u.role));

      const items: Responsable[] = eligibleUsers.map((user: any) => ({
        id: user.id,
        email: user.email,
        full_name: user.full_name || null,
        avatar_url: user.avatar_url || null,
        photo_avatar_url: user.photo_avatar_url || null,
        role: user.role,
        is_active: user.is_active,
        is_enabled_as_responsable: localConfig[user.id] ?? true, // por defecto habilitado
      }));

      return {
        items,
        total: items.length,
        enabled_count: items.filter(i => i.is_enabled_as_responsable).length,
      };
    } catch (fallbackError) {
      console.error('Error in fallback responsables config:', fallbackError);
      return { items: [], total: 0, enabled_count: 0 };
    }
  },

  /**
   * Actualizar responsables habilitados (bulk)
   * Solo admin puede acceder
   * PUT /admin/responsables/config
   */
  async updateResponsablesConfig(request: UpdateResponsablesRequest): Promise<ResponsablesConfigResponse> {
    try {
      const { data } = await api.put<ResponsablesConfigResponse>('/admin/responsables/config', request);
      return data;
    } catch (error: any) {
      console.error('Error updating responsables config:', error);
      // Fallback: guardar localmente
      if (error.response?.status === 404) {
        return await this.fallbackUpdateConfig(request);
      }
      throw error;
    }
  },

  /**
   * Toggle individual de un responsable
   * Solo admin puede acceder
   * PATCH /admin/responsables/{user_id}/toggle
   */
  async toggleResponsable(request: ToggleResponsableRequest): Promise<Responsable> {
    try {
      const { data } = await api.patch<Responsable>(
        `/admin/responsables/${request.user_id}/toggle`,
        { is_enabled: request.is_enabled }
      );
      return data;
    } catch (error: any) {
      console.error('Error toggling responsable:', error);
      // Fallback: guardar localmente
      if (error.response?.status === 404) {
        this.setLocalConfigEntry(request.user_id, request.is_enabled);
        // Re-fetch para devolver datos actualizados
        const config = await this.fallbackGetResponsablesConfig();
        const user = config.items.find(i => i.id === request.user_id);
        if (user) return user;
      }
      throw error;
    }
  },

  /**
   * Obtener solo los responsables habilitados (para uso en selectores de toda la app)
   * Cualquier usuario autenticado con rol admin/lawyer/agent puede acceder
   * GET /admin/responsables/enabled
   */
  async getEnabledResponsables(): Promise<EnabledResponsablesResponse> {
    try {
      const { data } = await api.get<EnabledResponsablesResponse>('/admin/responsables/enabled');
      return data;
    } catch (error: any) {
      console.error('Error getting enabled responsables:', error);
      // Fallback: usar config local
      if (error.response?.status === 404) {
        const config = await this.fallbackGetResponsablesConfig();
        const enabled = config.items.filter(i => i.is_enabled_as_responsable);
        return { items: enabled, total: enabled.length };
      }
      throw error;
    }
  },

  // ===== LOCAL STORAGE HELPERS (fallback hasta que el backend esté listo) =====

  /**
   * Obtener configuración local de responsables
   * Map de user_id -> is_enabled
   */
  getLocalConfig(): Record<string, boolean> {
    try {
      const stored = localStorage.getItem('responsables_config');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // ignore
    }
    return {};
  },

  /**
   * Guardar configuración local de responsables
   */
  setLocalConfig(config: Record<string, boolean>): void {
    try {
      localStorage.setItem('responsables_config', JSON.stringify(config));
    } catch {
      // ignore
    }
  },

  /**
   * Actualizar una entrada en la configuración local
   */
  setLocalConfigEntry(userId: string, isEnabled: boolean): void {
    const config = this.getLocalConfig();
    config[userId] = isEnabled;
    this.setLocalConfig(config);
  },

  /**
   * Fallback para actualizar configuración masiva
   */
  async fallbackUpdateConfig(request: UpdateResponsablesRequest): Promise<ResponsablesConfigResponse> {
    // Obtener todos los usuarios elegibles
    const config = await this.fallbackGetResponsablesConfig();
    
    // Crear nuevo mapa: solo los IDs en la lista están habilitados
    const newConfig: Record<string, boolean> = {};
    config.items.forEach(item => {
      newConfig[item.id] = request.enabled_user_ids.includes(item.id);
    });
    
    this.setLocalConfig(newConfig);
    
    // Re-fetch con la nueva config
    return await this.fallbackGetResponsablesConfig();
  },
};
